import { Button, Link, tokens } from "@blurb/codex-react";
import { createStyles } from "@mantine/core";
import { GraphQL, isGraphQLError } from "@rpi/openapi-api";
import { RpiAlert, useShowActionableSuccessToast, useShowErrorToast } from "@rpi/openapi-core";
import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { RpaDrawer } from "../../rpi-core/drawer/RpaDrawer";
import { Body, Label } from "../../components/Typography";
import { buildReasonOptions, getReasonLabel } from "./orderReasons";
import { RpaDropdown } from "../../components/inputFields/RpaDropdownInput";
import { RpaRadio, RpaRadioGroup } from "../../components/inputFields/RpaRadioInput";
import { ItemSelectionRow, OrderItemSelector } from "./OrderItemSelector";
import { routePaths } from "../../utils/route.util";
import { RequiredProperty } from "../../utils/type.util";

const SHIPPING_LABELS: Record<GraphQL.OpenapiProvidedShippingClassification, string> = {
  [GraphQL.OpenapiProvidedShippingClassification.Economy]: "Economy",
  [GraphQL.OpenapiProvidedShippingClassification.Standard]: "Standard",
  [GraphQL.OpenapiProvidedShippingClassification.Express]: "Express",
  [GraphQL.OpenapiProvidedShippingClassification.Priority]: "Priority",
};

const SHIPPING_OPTIONS = Object.entries(SHIPPING_LABELS).map(([value, label]) => ({ value, label }));

const useStyles = createStyles({
  body: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacing[4],
    padding: `0 ${tokens.spacing[6]}`,
  },
  footer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: tokens.spacing[3],
    padding: tokens.spacing[6],
  },
  cancelLink: {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 0,
    alignSelf: "center",
  },
  cancelText: {
    color: tokens.color.semantic.text.link,
    borderBottom: `1px solid currentColor`,
    paddingBottom: 2,
  },
});

type OrderByIdentifier = NonNullable<GraphQL.OrderRefundReprintInfoQuery["orderByIdentifier"]>;
// Callers must guard with `isOrderReprintable` before mounting the drawer.
export type OrderForReprint = RequiredProperty<OrderByIdentifier, "customer" | "customerOrderId" | "orderItems">;

export function isOrderReprintable(
  order: GraphQL.OrderRefundReprintInfoQuery["orderByIdentifier"] | undefined
): order is OrderForReprint {
  if (order === null || order === undefined) return false;
  const { customer, customerOrderId, orderItems } = order;
  return (
    customer !== null &&
    customer !== undefined &&
    customerOrderId !== null &&
    customerOrderId !== undefined &&
    orderItems !== null &&
    orderItems !== undefined
  );
}

interface ReprintDrawerProps {
  opened: boolean;
  onClose: () => void;
  customerPublicId: string;
  customerOrderId: string;
  isSandbox: boolean;
}

/** Project the generic selector rows into reprint's wire-format items. */
function collectValidReprintItems(rows: ItemSelectionRow[]): { orderItemId: number; quantity: number }[] {
  return rows.flatMap((row) => {
    if (!row.selectedItem) return [];
    const quantity = Number.parseInt(row.value, 10);
    if (!Number.isInteger(quantity) || quantity <= 0) return [];
    return [{ orderItemId: row.selectedItem.id, quantity }];
  });
}

function resolveShippingClassification(
  shippingMethodCode: string | undefined | null
): GraphQL.OpenapiProvidedShippingClassification | undefined {
  if (!shippingMethodCode) return undefined;
  const upper = shippingMethodCode.toUpperCase();
  const values: string[] = Object.values(GraphQL.OpenapiProvidedShippingClassification);
  if (values.includes(upper)) return upper as GraphQL.OpenapiProvidedShippingClassification;
  return undefined;
}

export const REPRINT_SUCCESS_MESSAGE = "The Reprint Order was created successfully!";

// Codex `Link` (plain anchor) — toast content lives outside the Router context.
export function useReprintSuccessToast() {
  const showActionableSuccessToast = useShowActionableSuccessToast();
  return (params: { newOrderId: string; customerPublicId: string }) => {
    const { newOrderId, customerPublicId } = params;
    const orderPath = routePaths.order({ customerPublicId, customerOrderId: newOrderId });
    showActionableSuccessToast(
      <>
        {REPRINT_SUCCESS_MESSAGE}{" "}
        <Link href={orderPath} data-testid="reprint-success-link">
          Go to Reprint Order
        </Link>
      </>
    );
  };
}

export function ReprintDrawer({ opened, onClose, customerPublicId, customerOrderId, isSandbox }: ReprintDrawerProps) {
  const showErrorToast = useShowErrorToast();
  const showReprintSuccess = useReprintSuccessToast();
  const queryClient = useQueryClient();

  const { mutate: submitReprint, isLoading } = GraphQL.useReprintOrderV2Mutation({
    onSuccess: async (data) => {
      showReprintSuccess({
        newOrderId: data.orderReprintCreateV2ByIdentifier.customerOrderId,
        customerPublicId,
      });
      await queryClient.invalidateQueries({ queryKey: ["Order"] });
      onClose();
    },
    onError: (error) => {
      if (isGraphQLError(error)) {
        showErrorToast(error.message);
      } else {
        showErrorToast("Failed to create reprint order. Please try again.");
      }
    },
  });

  const {
    data,
    isLoading: isOrderLoading,
    error,
  } = GraphQL.useOrderRefundReprintInfoQuery(
    {
      orderIdentifier: {
        customerPublicId,
        customerOrderId,
      },
      isSandbox,
    },
    {
      enabled: opened,
    }
  );
  const order = data?.orderByIdentifier;

  /** Prevent closing mid-submission to avoid orphaned requests. */
  const handleClose = () => {
    if (!isLoading) onClose();
  };

  return (
    <RpaDrawer
      title={`Reprint ${customerOrderId}`}
      opened={opened}
      onClose={handleClose}
      isLoading={isOrderLoading}
      error={error}
    >
      {order && isOrderReprintable(order) && (
        <ReprintDrawerContents order={order} isLoading={isLoading} onClose={handleClose} submit={submitReprint} />
      )}
    </RpaDrawer>
  );
}

interface ReprintDrawerContentProps {
  order: OrderForReprint;
  isLoading: boolean;
  submit: (input: GraphQL.ReprintOrderV2MutationVariables) => void;
  onClose: () => void;
}

// Separate child so internal state resets when the drawer closes.
function ReprintDrawerContents({ order, isLoading, submit, onClose }: ReprintDrawerContentProps) {
  const { classes } = useStyles();

  const [reason, setReason] = React.useState<string | undefined>(undefined);
  const [shippingClassification, setShippingClassification] =
    React.useState<GraphQL.OpenapiProvidedShippingClassification>(
      resolveShippingClassification(order.destination?.shippingMethodCode) ??
        GraphQL.OpenapiProvidedShippingClassification.Economy
    );
  const [isFreeOfCharge, setIsFreeOfCharge] = React.useState<boolean | undefined>(undefined);
  const [submitted, setSubmitted] = React.useState(false);

  const [rows, setRows] = React.useState<ItemSelectionRow[]>([]);

  const orderItems = order.orderItems;
  const validReprintItems = collectValidReprintItems(rows);

  const missingReason = !reason;
  const missingItems = validReprintItems.length === 0;
  const missingPayment = isFreeOfCharge === undefined;

  const handleSubmit = () => {
    setSubmitted(true);
    if (!reason || !shippingClassification || isFreeOfCharge === undefined || validReprintItems.length === 0) return;

    submit({
      input: {
        reason: getReasonLabel(reason),
        shippingClassification,
        isFreeOfCharge,
        orderIdentifier: {
          customerPublicId: order.customer.publicId,
          customerOrderId: order.customerOrderId,
        },
        reprintedItems: validReprintItems,
      },
    });
  };

  return (
    <>
      <div className={classes.body}>
        <RpaDropdown
          label={
            <Label size="base" weight="semibold">
              Reason
            </Label>
          }
          placeholder="Select.."
          searchable
          creatable
          getCreateLabel={(query) => query}
          onCreate={(query) => {
            setReason(query);
            return query;
          }}
          data={buildReasonOptions()}
          value={reason ?? null}
          onChange={(val) => setReason(val ?? undefined)}
          error={submitted && missingReason ? "Please select a reason" : undefined}
        />

        <RpaDropdown
          label={
            <Label size="base" weight="semibold">
              Shipping Method
            </Label>
          }
          placeholder="Select.."
          data={SHIPPING_OPTIONS}
          value={shippingClassification}
          onChange={(val) => {
            const match = Object.values(GraphQL.OpenapiProvidedShippingClassification).find((v) => v === val);
            if (match) setShippingClassification(match);
          }}
        />

        <OrderItemSelector
          orderItems={orderItems}
          rows={rows}
          onRowsChange={setRows}
          valueColumn={{ kind: "quantity", label: "Qty." }}
          error={submitted && missingItems ? "Please add an item" : undefined}
        />

        <div>
          <RpaRadioGroup
            label={
              <Label size="base" weight="semibold">
                Payment Method
              </Label>
            }
            value={isFreeOfCharge === undefined ? "" : isFreeOfCharge ? "free" : "charge"}
            onChange={(val) => setIsFreeOfCharge(val === "free")}
          >
            <RpaRadio value="charge" label="Charge as Normal" />
            <RpaRadio value="free" label="No Payment - No Cost Reprint" />
          </RpaRadioGroup>
          {submitted && missingPayment && <RpiAlert>Please select the payment method</RpiAlert>}
        </div>
      </div>
      <RpaDrawer.Footer>
        <div className={classes.footer}>
          <Button variant="filled" intent="primary" size="large" loading={isLoading} onClick={handleSubmit}>
            Submit Reprint
          </Button>
          <button className={classes.cancelLink} onClick={onClose} disabled={isLoading}>
            <Body size="base" weight="semibold" className={classes.cancelText}>
              Cancel
            </Body>
          </button>
        </div>
      </RpaDrawer.Footer>
    </>
  );
}
